import React, { useEffect, useRef, useState } from 'react';
import { Activity, BookOpen, Cpu, Hammer, Info, Layers, MapPin, Microscope, MousePointer, Plus, Route, Ruler, ShieldAlert, Square, X, Zap } from 'lucide-react';
import { AgentType, NodeType } from '../types';
import { AGENT_COLORS, NODE_COLORS } from '../constants';
import { AGENT_LABELS, NODE_LABELS } from '../utils/translations';
import { ICON_METADATA } from './BuildPanel';

type TabId = 'BASICS' | 'BUILD' | 'SIM' | 'LEGEND';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: TabId;
}

const HELP_TABS = [
  { id: 'BASICS' as const, label: 'Apžvalga', icon: BookOpen, activeClass: 'text-cyan-400 border-cyan-500 bg-cyan-500/10', indicatorClass: 'bg-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.8)]' },
  { id: 'BUILD' as const, label: 'Architektūra', icon: Hammer, activeClass: 'text-cyan-400 border-cyan-500 bg-cyan-500/10', indicatorClass: 'bg-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.8)]' },
  { id: 'SIM' as const, label: 'Simuliacija', icon: Cpu, activeClass: 'text-emerald-400 border-emerald-500 bg-emerald-500/10', indicatorClass: 'bg-emerald-400 shadow-[0_0_15px_rgba(52,211,153,0.8)]' },
  { id: 'LEGEND' as const, label: 'Sutartiniai ženklai', icon: Info, activeClass: 'text-purple-400 border-purple-500 bg-purple-500/10', indicatorClass: 'bg-purple-400 shadow-[0_0_15px_rgba(192,132,252,0.8)]' }
];

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose, initialTab = 'BASICS' }) => {
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const parent = dialog?.parentElement;
    const backgroundElements = parent
      ? Array.from(parent.children).filter((element): element is HTMLElement => element instanceof HTMLElement && element !== dialog)
      : [];
    const previousBackgroundState = backgroundElements.map(element => ({
      element,
      inert: element.hasAttribute('inert'),
      ariaHidden: element.getAttribute('aria-hidden')
    }));
    backgroundElements.forEach(element => {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    });

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusFrame = requestAnimationFrame(() => closeButtonRef.current?.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousBackgroundState.forEach(({ element, inert, ariaHidden }) => {
        if (!inert) element.removeAttribute('inert');
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      previouslyFocused?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-dialog-title"
      tabIndex={-1}
      className="fixed inset-0 z-[1000] flex flex-col overflow-hidden bg-slate-950 text-slate-200 animate-in fade-in duration-300"
    >
      <div className="absolute inset-0 pointer-events-none opacity-20" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(6,182,212,0.15),transparent)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.03),rgba(0,255,0,0.01),rgba(0,0,255,0.03))] bg-[length:100%_4px,3px_100%]" />
      </div>

      <header className="relative z-10 flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-slate-900/50 px-4 py-4 backdrop-blur-md sm:px-6 lg:px-12 lg:py-6">
        <div className="flex min-w-0 items-center gap-3 sm:gap-5">
          <div className="hidden rounded-lg border border-cyan-500/40 bg-cyan-500/20 p-3 shadow-[0_0_20px_rgba(6,182,212,0.3)] sm:block">
            <Activity className="text-cyan-400" size={28} />
          </div>
          <div className="min-w-0">
            <h1 id="help-dialog-title" className="truncate font-rajdhani text-xl font-bold uppercase tracking-[0.12em] text-white sm:text-2xl sm:tracking-[0.22em] lg:text-3xl">
              SMPS perkėlimas <span className="ml-2 text-[10px] font-mono text-cyan-500 opacity-60 sm:text-xs">v2.5.0</span>
            </h1>
            <p className="mt-1 text-[9px] font-mono uppercase tracking-widest text-slate-400 sm:text-xs">Naudotojo instrukcija</p>
          </div>
        </div>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className="group flex shrink-0 items-center gap-2 border border-red-500/30 bg-red-500/5 px-3 py-2 text-red-400 transition-all hover:bg-red-500/20 focus:outline-none focus:ring-2 focus:ring-red-400 sm:px-5 sm:py-3"
        >
          <X size={18} /> <span className="hidden font-rajdhani text-xs font-bold uppercase tracking-widest sm:inline">Uždaryti</span>
        </button>
      </header>

      <nav role="tablist" aria-label="Instrukcijos skyriai" className="relative z-10 flex shrink-0 overflow-x-auto border-b border-white/10 bg-black/40 px-2 custom-scrollbar sm:px-6 lg:px-12">
        {HELP_TABS.map(tab => (
          <button
            key={tab.id}
            id={`help-tab-${tab.id}`}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`help-panel-${tab.id}`}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`relative flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-4 font-rajdhani text-xs font-bold uppercase tracking-wider transition-all sm:px-6 lg:px-10 lg:py-5 lg:text-sm ${
              activeTab === tab.id ? tab.activeClass : 'border-transparent text-slate-500 hover:bg-white/5 hover:text-slate-300'
            }`}
          >
            <tab.icon size={17} /> {tab.label}
            {activeTab === tab.id && <span className={`absolute bottom-[-2px] left-0 h-[2px] w-full ${tab.indicatorClass}`} aria-hidden="true" />}
          </button>
        ))}
      </nav>

      <main
        id={`help-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`help-tab-${activeTab}`}
        className="relative z-10 flex-1 overflow-y-auto p-4 custom-scrollbar sm:p-8 lg:p-12"
      >
        <div className="mx-auto max-w-[1200px] space-y-12">
          {activeTab === 'BASICS' && (
            <div className="space-y-10 animate-in slide-in-from-bottom-4 duration-500">
              <section className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
                <div className="space-y-6 lg:col-span-7">
                  <h2 className="border-l-4 border-cyan-500 pl-4 font-rajdhani text-2xl font-bold uppercase tracking-widest text-white sm:text-4xl">Darbo pradžia</h2>
                  <p className="text-sm font-light leading-relaxed text-slate-300 sm:text-lg">
                    Įrankis skirtas skubios pagalbos skyriaus planui kurti, pacientų ir personalo judėjimui modeliuoti, atstumams vertinti bei infekcijos rizikai analizuoti.
                  </p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="rounded-2xl border border-white/5 bg-slate-900/50 p-5">
                      <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase text-cyan-400"><Hammer size={14}/> Architekto režimas</h3>
                      <p className="text-xs leading-relaxed text-slate-400">Įkelkite brėžinį, nustatykite mastelį, kurkite patalpas, zonas, sienas, ikonas ir maršrutus.</p>
                    </div>
                    <div className="rounded-2xl border border-white/5 bg-slate-900/50 p-5">
                      <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase text-emerald-400"><Cpu size={14}/> Simuliacijos režimas</h3>
                      <p className="text-xs leading-relaxed text-slate-400">Valdykite pacientų srautą, personalą, scenarijus, elgsenos parametrus ir analitikos ataskaitą.</p>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-5 sm:p-6">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-cyan-400"><MousePointer size={16}/> Navigacija ir spartieji klavišai</h3>
                    <div className="space-y-2 font-mono text-[10px] uppercase">
                      {[
                        ['Pelės ratukas', 'Priartinti / nutolinti'],
                        ['Tuščios drobės tempimas', 'Slinkti vaizdą'],
                        ['Space + tempimas', 'Laikinai slinkti'],
                        ['Vidurinis pelės mygtukas', 'Slinkti vaizdą'],
                        ['H', 'Slinkimo įrankis'],
                        ['Shift + tempimas tuščioje vietoje', 'Pasirinkimo rėmas'],
                        ['Esc', 'Pasirinkti / išvalyti pasirinkimą'],
                        ['Ctrl/Cmd + Z', 'Atšaukti'],
                        ['Ctrl/Cmd + Shift + Z arba Y', 'Grąžinti']
                      ].map(([key, action]) => (
                        <div key={key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3 border-b border-white/5 pb-2 last:border-0"><span>{key}</span><span className="text-right text-white">{action}</span></div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'BUILD' && (
            <div className="grid grid-cols-1 gap-8 animate-in slide-in-from-right-4 duration-500 lg:grid-cols-3">
              <section className="space-y-10 lg:col-span-2">
                <div className="space-y-6">
                  <h3 className="flex items-center gap-3 font-rajdhani text-2xl font-bold uppercase tracking-widest text-cyan-400"><Hammer size={24}/> Infrastruktūros kūrimas</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {[
                      { icon: Plus, color: 'text-cyan-400', title: 'Patalpos (mazgai)', text: 'Taškiniai simuliacijos objektai, turintys talpą, apdorojimo laiką ir greičio koeficientą. Simuliacijos ryšiais sujunkite juos į judėjimo grafą.' },
                      { icon: Square, color: 'text-emerald-400', title: 'Zonos', text: 'Stačiakampės ar sudėtinės patalpų ribos. Pasirinktą zoną keiskite 8 rankenėlėmis; laikant Shift išlaikomos proporcijos.' },
                      { icon: Route, color: 'text-purple-400', title: 'Maršrutai', text: '„Pac. kelias“ skirtas planavimui, „Sim. ryšys“ – agentų judėjimui. Pieštuku įjunkite taškų redagavimą; dvigubu segmento paspaudimu pridėkite tašką.' },
                      { icon: MapPin, color: 'text-amber-400', title: 'Ikonos ir sienos', text: 'Ikonos padeda orientuotis, o sienos žymi fizines ribas. Šie objektai gali būti pasirenkami, perkeliami, kopijuojami ir trinami.' }
                    ].map(item => (
                      <div key={item.title} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                        <item.icon className={`${item.color} mb-3`} size={24}/>
                        <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-white">{item.title}</h4>
                        <p className="text-xs leading-relaxed text-slate-400">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-3 rounded-2xl border border-cyan-500/20 bg-slate-900/60 p-6">
                    <h4 className="flex items-center gap-2 text-sm font-bold uppercase text-white"><MousePointer size={18} className="text-cyan-400"/> Pasirinkimas ir transformavimas</h4>
                    <ul className="space-y-2 text-xs leading-relaxed text-slate-300">
                      <li>• Paspaudimas pasirenka vieną objektą; Shift + paspaudimas keičia grupinį pasirinkimą.</li>
                      <li>• Shift + tempimas tuščioje vietoje piešia pasirinkimo rėmą.</li>
                      <li>• Tempkite pasirinktą objektą ar visą grupę. Shift tempimo metu išjungia pritraukimą.</li>
                      <li>• Ctrl/Cmd+A pasirenka visus architektūros objektus.</li>
                    </ul>
                  </div>
                  <div className="space-y-3 rounded-2xl border border-purple-500/20 bg-slate-900/60 p-6">
                    <h4 className="flex items-center gap-2 text-sm font-bold uppercase text-white"><Layers size={18} className="text-purple-400"/> Kopijavimas ir istorija</h4>
                    <ul className="space-y-2 text-xs leading-relaxed text-slate-300">
                      <li>• Ctrl/Cmd+C – kopijuoti; Ctrl/Cmd+V – įklijuoti.</li>
                      <li>• Ctrl/Cmd+D – dubliuoti; Delete – pašalinti.</li>
                      <li>• Ctrl/Cmd+Z – atšaukti; Ctrl/Cmd+Shift+Z arba Ctrl/Cmd+Y – grąžinti.</li>
                      <li>• Įklijuotos kopijos automatiškai pastumiamos ir gauna naujus ID.</li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-3 rounded-2xl border border-cyan-500/20 bg-slate-900/60 p-6">
                  <h4 className="flex items-center gap-2 text-sm font-bold uppercase text-white"><Layers size={18} className="text-cyan-400"/> Zonų valdymas</h4>
                  <p className="text-xs leading-relaxed text-slate-300">Zonos automatiškai grupuojamos pagal tipą. Galite jas pasirinkti, pervadinti, ištrinti, matyti grupės plotą m² ir sujungti persidengiančius tos pačios rūšies stačiakampius funkcija „Automatinis suliejimas“.</p>
                </div>
              </section>

              <aside className="space-y-6">
                <div className="space-y-5 rounded-3xl border border-amber-500/20 bg-slate-900/80 p-6 shadow-2xl sm:p-8">
                  <h3 className="flex items-center gap-3 text-lg font-bold uppercase tracking-widest text-amber-500"><Ruler size={24}/> Mastelio kalibravimas</h3>
                  {[
                    ['1', 'Pasirinkite „Mastelis“', 'Įrankį rasite pagrindinių įrankių juostoje.'],
                    ['2', 'Paspauskite du žinomus taškus', 'Pažymėkite atkarpos pradžią ir pabaigą, pavyzdžiui, durų kraštus.'],
                    ['3', 'Įveskite tikrą atstumą metrais', 'Pagal naują mastelį perskaičiuojami rodomi atstumai, plotai ir fizinio greičio reikšmės.']
                  ].map(([step, title, text]) => (
                    <div key={step} className="space-y-1">
                      <div className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-amber-500/20 text-[10px] font-bold text-amber-500">{step}</span><p className="text-xs font-bold text-slate-300">{title}</p></div>
                      <p className="ml-9 text-[10px] text-slate-500">{text}</p>
                    </div>
                  ))}
                  <div className="border-t border-white/10 pt-4 text-[10px] leading-relaxed text-slate-400"><strong className="text-cyan-300">Matuoti:</strong> pasirinkite „Matuoti“ ir paspauskite du taškus – atstumas bus parodytas metrais.</div>
                </div>
              </aside>
            </div>
          )}

          {activeTab === 'SIM' && (
            <div className="space-y-10 animate-in slide-in-from-left-4 duration-500">
              <section className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="space-y-5 rounded-3xl border border-emerald-500/20 bg-slate-900/60 p-6 sm:p-8">
                  <h3 className="flex items-center gap-3 text-xl font-bold uppercase tracking-widest text-emerald-400"><Zap size={24}/> Agentų logika</h3>
                  <p className="text-xs leading-relaxed text-slate-300">Pacientai juda per simuliacijos ryšiais sujungtas patalpas. Įprastas srautas yra įėjimas → triažas → laukimas → kabinetas, tačiau pagal būklę ir nustatymus gali būti įtraukti diagnostikos, traumų, vaistinės ar išėjimo etapai.</p>
                  <div className="space-y-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4 font-mono text-[9px] uppercase text-emerald-300"><p>● Trauma / insultas: aukščiausias prioritetas</p><p>● GMP pacientas: vidutinis prioritetas</p><p>● Savarankiškai atvykęs: žemesnis prioritetas</p></div>
                </div>
                <div className="space-y-5 rounded-3xl border border-red-500/20 bg-slate-900/60 p-6 sm:p-8">
                  <h3 className="flex items-center gap-3 text-xl font-bold uppercase tracking-widest text-red-400"><Microscope size={24}/> Infekcijų modelis</h3>
                  <p className="text-xs leading-relaxed text-slate-300">Infekuoti agentai skleidžia užkratą, o patalpų mazgai kaupia užterštumą. Sveikas agentas gali užsikrėsti būdamas infekcijos spindulyje arba užterštoje patalpoje.</p>
                  <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4 text-[10px] leading-relaxed text-red-200"><strong>Sklaidos mažinimas:</strong> atverkite „Simuliacija → Elgsenos protokolai“ ir reguliuokite infekcijos tikimybę, spindulį bei užterštumo vengimą.</div>
                </div>
                <div className="space-y-5 rounded-3xl border border-cyan-500/20 bg-slate-900/60 p-6 sm:p-8">
                  <h3 className="flex items-center gap-3 text-xl font-bold uppercase tracking-widest text-cyan-400"><ShieldAlert size={24}/> Masinių įvykių scenarijai</h3>
                  <p className="text-xs leading-relaxed text-slate-300">MCI – masinės nelaimės arba didelio pacientų antplūdžio įvykis. „Epidemija“ generuoja infekuotų pacientų srautą, o „Avarija“ – didelį trauminių pacientų srautą.</p>
                  <p className="text-[10px] leading-relaxed text-slate-500">Prieš paleisdami scenarijų įsitikinkite, kad plane yra įėjimo mazgas ir simuliacijos ryšiais sujungtas maršrutas.</p>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'LEGEND' && (
            <div className="space-y-12 animate-in zoom-in-95 duration-500">
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
                <section className="space-y-6">
                  <h3 className="flex items-center gap-3 border-b border-white/10 pb-4 text-xl font-bold uppercase tracking-widest text-white"><Square size={20} className="text-cyan-400"/> Patalpų tipai</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {Object.entries(NODE_COLORS).map(([type, color]) => (
                      <div key={type} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-3"><div className="h-4 w-4 rounded-full" style={{ backgroundColor: color }}/><span className="text-[9px] font-bold uppercase tracking-wide text-slate-200">{NODE_LABELS[type as NodeType]}</span></div>
                    ))}
                  </div>
                </section>
                <section className="space-y-6">
                  <h3 className="flex items-center gap-3 border-b border-white/10 pb-4 text-xl font-bold uppercase tracking-widest text-white"><Activity size={20} className="text-emerald-400"/> Agentų vaidmenys</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {Object.entries(AGENT_COLORS).map(([type, color]) => (
                      <div key={type} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-3"><div className="h-6 w-6 rounded-full border-2 border-white/20" style={{ backgroundColor: color }}/><span className="text-[9px] font-bold uppercase text-slate-200">{AGENT_LABELS[type as AgentType]}</span></div>
                    ))}
                  </div>
                </section>
              </div>

              <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
                <section className="space-y-6">
                  <h3 className="flex items-center gap-3 border-b border-white/10 pb-4 text-xl font-bold uppercase tracking-widest text-white"><MapPin size={20} className="text-amber-400"/> Brėžinio ikonos</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {Object.entries(ICON_METADATA).map(([type, metadata]) => {
                      const IconComponent = metadata.icon;
                      return <div key={type} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-3"><IconComponent size={17} color={metadata.color}/><span className="text-[9px] font-bold uppercase text-slate-200">{metadata.label}</span></div>;
                    })}
                  </div>
                </section>
                <section className="space-y-6">
                  <h3 className="flex items-center gap-3 border-b border-white/10 pb-4 text-xl font-bold uppercase tracking-widest text-white"><Route size={20} className="text-purple-400"/> Linijos ir redagavimas</h3>
                  <div className="space-y-3 text-xs text-slate-300">
                    <div className="rounded-xl border border-white/5 bg-white/5 p-4"><span className="mr-3 inline-block h-[3px] w-12 bg-slate-200 align-middle"/> Siena</div>
                    <div className="rounded-xl border border-white/5 bg-white/5 p-4"><span className="mr-3 inline-block h-[3px] w-12 bg-cyan-400 align-middle"/> Planavimo arba simuliacijos maršrutas; tipas rodomas maršrutų sąraše</div>
                    <div className="rounded-xl border border-white/5 bg-white/5 p-4"><span className="mr-3 inline-block w-12 border-t-2 border-dashed border-cyan-400 align-middle"/> Matavimo arba pasirinkimo pagalbinė linija</div>
                    <div className="rounded-xl border border-white/5 bg-white/5 p-4"><span className="mr-3 inline-block h-3 w-3 rounded-sm border-2 border-cyan-400 bg-slate-950 align-middle"/> Zonos arba maršruto redagavimo rankenėlė</div>
                  </div>
                </section>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="relative z-10 flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-white/10 bg-slate-900/80 px-4 py-3 font-mono text-[9px] uppercase tracking-widest text-slate-500 sm:px-6 lg:px-12">
        <span>SMPS perkėlimas v2.5.0</span><span className="text-cyan-500">Naudotojo instrukcija</span>
      </footer>
    </div>
  );
};

export default HelpModal;
